import { useMemo } from 'react';

import type { WallBoard, WallCard } from '@walls/store';
import { getCardFrameDimensions } from '../cards/utils/cardGeometry';
import { getBoardCardTree, sortByZPosition } from '../selectors/boardCardSelectors';
import { BoardCard } from '../cards/BoardCard';
import { BoardCluster } from '../clusters/components/BoardCluster';

interface BoardRect {
  height: number;
  width: number;
  x: number;
  y: number;
}

interface BoardCardsLayerProps {
  board: WallBoard;
  cards: WallCard[];
  visibleRect?: BoardRect | null;
}

const cardRenderOverscan = 800;

export function BoardCardsLayer({ board, cards, visibleRect }: BoardCardsLayerProps) {
  const orderedElements = useMemo(() => {
    const { rootCards, rootClusters } = getBoardCardTree(cards);

    return sortByZPosition([...rootClusters, ...rootCards]).filter((card) =>
      isCardInVisibleRect(card, visibleRect)
    );
  }, [cards, visibleRect]);

  const renderedElements = useMemo(
    () =>
      orderedElements.map((card) =>
        card.persistCluster || card.cards?.length ? (
          <BoardCluster key={card.id} board={board} cluster={card} />
        ) : (
          <BoardCard key={card.id} board={board} card={card} />
        )
      ),
    [board, orderedElements]
  );

  return renderedElements;
}

function isCardInVisibleRect(card: WallCard, visibleRect?: BoardRect | null) {
  if (!visibleRect || card.xPos == null || card.yPos == null) {
    return true;
  }

  const cardRect = getCardRenderRect(card);
  const expandedVisibleRect = {
    height: visibleRect.height + cardRenderOverscan * 2,
    width: visibleRect.width + cardRenderOverscan * 2,
    x: visibleRect.x - cardRenderOverscan,
    y: visibleRect.y - cardRenderOverscan,
  };

  return rectsIntersect(cardRect, expandedVisibleRect);
}

function getCardRenderRect(card: WallCard): BoardRect {
  const childCount = card.cards?.length ?? 0;
  const isExpandedCluster = (card.persistCluster || childCount > 0) && !card.collapsed;
  const dimensions = isExpandedCluster
    ? {
        height: card.height ?? Math.max(180, childCount * 180 + 80),
        width: card.width ?? 260,
      }
    : getCardFrameDimensions(card);

  return {
    height: dimensions.height ?? 180,
    width: dimensions.width,
    x: card.xPos ?? 0,
    y: card.yPos ?? 0,
  };
}

function rectsIntersect(first: BoardRect, second: BoardRect) {
  return (
    first.x < second.x + second.width &&
    first.x + first.width > second.x &&
    first.y < second.y + second.height &&
    first.y + first.height > second.y
  );
}
